/**
 * Tema claro/escuro/sistema.
 *
 * O claro é o padrão — o modo "documento", confortável para longas sessões (e não-branco,
 * para não cansar a vista). O escuro é o modo "console". E há **Sistema**: seguir o
 * `prefers-color-scheme` do SO, reagindo quando ele muda.
 *
 * A ESCOLHA ('light' | 'dark' | 'system') vive no `localStorage` (para não piscar no
 * carregamento) e, quando logado, é sincronizada com a conta pelo AppShell. O tema
 * EFETIVO ('light' | 'dark') é o que de fato pinta a tela — é ele que o ícone do header mostra.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type Escolha = 'dark' | 'light' | 'system';
export type Efetivo = 'dark' | 'light';
const CHAVE = 'bhunter_tema';

const TemaContexto = createContext<{
  escolha: Escolha;
  efetivo: Efetivo;
  definir: (e: Escolha) => void;
} | null>(null);

function escolhaInicial(): Escolha {
  const salvo = localStorage.getItem(CHAVE);
  if (salvo === 'dark' || salvo === 'light' || salvo === 'system') return salvo;
  return 'light';
}

function sistemaEscuro(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export function ProvedorTema({ children }: { children: ReactNode }) {
  const [escolha, setEscolha] = useState<Escolha>(escolhaInicial);
  const [sysDark, setSysDark] = useState<boolean>(sistemaEscuro);

  // Enquanto a escolha for "system", acompanhamos o SO em tempo real.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const aoMudar = () => setSysDark(mq.matches);
    mq.addEventListener('change', aoMudar);
    return () => mq.removeEventListener('change', aoMudar);
  }, []);

  const efetivo: Efetivo = escolha === 'system' ? (sysDark ? 'dark' : 'light') : escolha;

  useEffect(() => {
    const raiz = document.documentElement;
    raiz.setAttribute('data-theme', efetivo);
    localStorage.setItem(CHAVE, escolha);
    // A barra do navegador acompanha o fundo — lido do token computado, nunca escrito à mão.
    const fundo = getComputedStyle(raiz).getPropertyValue('--bg').trim();
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', fundo);
  }, [efetivo, escolha]);

  const definir = useCallback((e: Escolha) => setEscolha(e), []);

  return (
    <TemaContexto.Provider value={{ escolha, efetivo, definir }}>{children}</TemaContexto.Provider>
  );
}

export function useTema() {
  const ctx = useContext(TemaContexto);
  if (!ctx) throw new Error('useTema precisa estar dentro de <ProvedorTema>');
  return ctx;
}
